import React, { useState, useEffect } from 'react';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import {
    Calendar,
    Search,
    Filter,
    Download,
    ShieldAlert,
    Clock,
    User,
    Activity,
    BarChart3,
    ChevronDown,
    Loader,
    Trash2
} from 'lucide-react';
import { formatDate } from '../../src/lib/utils';
import { useToast } from '../../src/contexts/ToastContext';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import UsoView from './UsoView';

interface Profile {
    id: string;
    full_name: string;
    email: string;
}

interface Log {
    id: string;
    user_id: string;
    action: string;
    details: any;
    created_at: string;
    user_email?: string; // We'll join or fetch this
}

const LogsView: React.FC = () => {
    const { profile } = useAuth();
    const [logs, setLogs] = useState<Log[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [deletingLogs, setDeletingLogs] = useState(false);
    const [negotiationNames, setNegotiationNames] = useState<Record<string, string>>({});
    const { addToast } = useToast();

    // Pagination
    const PAGE_SIZE = 100;
    const [hasMore, setHasMore] = useState(true);

    // Filters
    const [actionFilter, setActionFilter] = useState('');

    // Date filter — default to current month or year 
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const [startDate, setStartDate] = useState(firstDay.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(lastDay.toISOString().split('T')[0]);

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [selectedUserId, setSelectedUserId] = useState('all');

    // Aba "Uso" vive aqui dentro (não é rota própria) — mesma tela, mesmo
    // gate de acesso já resolvido abaixo.
    const [aba, setAba] = useState<'logs' | 'uso'>('logs');

    useEffect(() => {
        const allowedRoles = ['adm', 'gerente_parcerias', 'parceiro'];
        if (profile?.role && allowedRoles.includes(profile.role)) {
            setIsAdmin(profile.role === 'adm'); // isAdmin name is slightly misleading now, but we'll use it for "isAuthorizedToManageLogs" or keep it if it's just for certain UI elements
            fetchProfiles();
            fetchLogs(true);
        } else {
            setIsAdmin(false);
            setLoading(false);
        }
    }, [profile]);

    const fetchProfiles = async () => {
        // Conta de consumidor (Portal) nunca entra em lista de escolha do sistema.
        let query = supabase.from('profiles').select('*').neq('role', 'consumidor');

        if (profile?.role === 'gerente') {
            query = query.in('role', ['parceiro', 'vendedor', 'vendedor_parceiro']);
        } else if (profile?.role === 'gerente_parcerias') {
            query = query.in('role', ['parceiro', 'vendedor_parceiro']);
        } else if (profile?.role === 'parceiro') {
            query = query.eq('parceiro_id', profile.id);
        }

        const { data } = await query.order('full_name');
        if (data) setProfiles(data);
    };

    const fetchLogs = async (isInitial = false) => {
        if (isInitial) {
            setLoading(true);
            setLogs([]); // Clear logs on new search
        } else {
            setLoadingMore(true);
        }

        try {
            let matchedNegociacaoIds: number[] = [];
            if (actionFilter) {
                // Determinamos se o filtro de texto pode coincidir com os nomes de alguma negociação
                const { data: negData, error: negError } = await supabase
                    .from('negociacoes')
                    .select('id')
                    .ilike('titulo', `%${actionFilter}%`);
                
                if (!negError && negData) {
                    matchedNegociacaoIds = negData.map(n => n.id);
                }
            }

            let query = supabase
                .from('system_logs')
                .select(`
                    *,
                    profiles:user_id (full_name)
                `)
                .order('created_at', { ascending: false });

            if (actionFilter) {
                if (matchedNegociacaoIds.length > 0) {
                    const idsStr = matchedNegociacaoIds.join(',');
                    query = query.or(`action.ilike.%${actionFilter}%,details->>negociacao_id.in.(${idsStr})`);
                } else {
                    query = query.ilike('action', `%${actionFilter}%`);
                }
            }

            // Date filtering logic
            if (startDate) {
                query = query.gte('created_at', `${startDate}T00:00:00`);
            }
            if (endDate) {
                query = query.lte('created_at', `${endDate}T23:59:59`);
            }

            if (selectedUserId && selectedUserId !== 'all') {
                query = query.eq('user_id', selectedUserId);
            } else if (profile?.role !== 'adm') {
                // Hierarchical log filtering
                if (profile?.role === 'gerente') {
                    // Logic to stay within scope of system_logs for specific roles
                    const roleScopedProfiles = profiles.map(p => p.id);
                    if (roleScopedProfiles.length > 0) {
                        query = query.in('user_id', roleScopedProfiles);
                    } else {
                        // If no profiles found in scope, ensure no logs are returned
                        query = query.eq('user_id', '00000000-0000-0000-0000-000000000000');
                    }
                } else if (profile?.role === 'gerente_parcerias') {
                    const roleScopedProfiles = profiles.map(p => p.id);
                    if (roleScopedProfiles.length > 0) {
                        query = query.in('user_id', roleScopedProfiles);
                    } else {
                        query = query.eq('user_id', '00000000-0000-0000-0000-000000000000');
                    }
                } else if (profile?.role === 'parceiro') {
                    const roleScopedProfiles = profiles.map(p => p.id);
                    if (roleScopedProfiles.length > 0) {
                        query = query.in('user_id', roleScopedProfiles);
                    } else {
                        query = query.eq('user_id', '00000000-0000-0000-0000-000000000000');
                    }
                }
            }

            // Pagination using current logs length
            const from = isInitial ? 0 : logs.length;
            const to = from + PAGE_SIZE - 1;

            query = query.range(from, to);

            const { data, error } = await query;

            if (error) {
                console.error('Error fetching logs:', error);
            } else {
                const newLogs = data || [];
                
                // Fetch negotiation names for these logs if we don't hold them yet
                const negIdsToFetch = Array.from(new Set(newLogs.map(l => l.details?.negociacao_id).filter(id => id != null)));
                if (negIdsToFetch.length > 0) {
                    // Check state to avoid refetching
                    setNegotiationNames(prev => {
                        const missingIds = negIdsToFetch.filter(id => !prev[String(id)]);
                        if (missingIds.length > 0) {
                            // Asynchronously fetch remaining
                            supabase
                                .from('negociacoes')
                                .select('id, titulo')
                                .in('id', missingIds)
                                .then(({ data: namesData, error: namesError }) => {
                                    if (!namesError && namesData) {
                                        setNegotiationNames(currentPrev => {
                                            const newNames = { ...currentPrev };
                                            namesData.forEach(n => newNames[String(n.id)] = n.titulo);
                                            return newNames;
                                        });
                                    }
                                });
                        }
                        return prev;
                    });
                }

                if (isInitial) {
                    setLogs(newLogs);
                } else {
                    setLogs(prev => [...prev, ...newLogs]);
                }

                // Check if we reached the end
                if (newLogs.length < PAGE_SIZE) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            }
        } catch (error) {
            console.error('Unexpected error:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Debounce refetch or manual trigger? Let's use manual filter apply or effect
    useEffect(() => {
        const allowedRoles = ['adm', 'gerente_parcerias', 'parceiro'];
        if (profile?.role && allowedRoles.includes(profile.role)) {
            const timer = setTimeout(() => {
                fetchLogs(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [actionFilter, startDate, endDate, selectedUserId, profile]);

    const handleDeleteFilteredLogs = async () => {
        if (logs.length === 0) return;

        if (!window.confirm(`Tem certeza que deseja apagar os ${logs.length} logs atualmente exibidos na tela?`)) {
            return;
        }

        setDeletingLogs(true);
        try {
            const logIds = logs.map(l => l.id);
            const { data, error } = await supabase.functions.invoke('manage-users', {
                body: { action: 'delete_logs', logIds }
            });

            if (error) throw error;
            if (data && data.success === false) {
                throw new Error(data.error || data.message || 'Erro desconhecido no servidor');
            }

            addToast(`${logs.length} logs apagados com sucesso.`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'logs', acao: 'excluir_logs', origem: 'acao' });
            fetchLogs(true);
        } catch (error: any) {
            console.error('Erro ao apagar logs:', error);
            addToast('Erro ao apagar logs: ' + error.message, 'error');
        } finally {
            setDeletingLogs(false);
        }
    };

    const allowedRoles = ['adm', 'gerente_parcerias', 'parceiro'];
    if (!profile?.role || !allowedRoles.includes(profile.role)) {
        if (!loading) {
            return (
                <div className="flex flex-col items-center justify-center h-96 text-center animate-fade-in">
                    <ShieldAlert size={64} className="text-red-500 mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800">Acesso Negado</h2>
                    <p className="text-gray-600 mt-2">Esta área é restrita para usuários autorizados.</p>
                </div>
            );
        }
        return null; // Loading state handled by the main content
    }

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-poppins font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                        {aba === 'logs' ? <Activity className="text-primary" /> : <BarChart3 className="text-primary" />}
                        {aba === 'logs' ? 'Logs do Sistema' : 'Uso do Sistema'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {aba === 'logs' ? 'Monitore as atividades e auditoria do sistema.' : 'Veja a % de uso medido de cada módulo, funcionalidade e ação do sistema.'}
                    </p>
                </div>

                {aba === 'logs' && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDeleteFilteredLogs}
                            disabled={deletingLogs || logs.length === 0}
                            className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 px-3 py-1 rounded-full border border-red-100 dark:border-red-900/50 transition-colors disabled:opacity-50"
                            title="Apagar todos os logs que correspondem ao filtro atual na tela"
                        >
                            {deletingLogs ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            <span>Apagar Logs Filtrados</span>
                        </button>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full border border-blue-100 dark:border-blue-900/50">
                            <Clock size={14} />
                            <span>Mostrando últimos 100 registros</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Abas: Logs (auditoria) | Uso (telemetria) */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
                <button
                    onClick={() => setAba('logs')}
                    className={`px-4 py-1.5 text-sm rounded-md transition-colors ${aba === 'logs' ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                >
                    Logs
                </button>
                <button
                    onClick={() => setAba('uso')}
                    className={`px-4 py-1.5 text-sm rounded-md transition-colors ${aba === 'uso' ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                >
                    Uso
                </button>
            </div>

            {aba === 'uso' && <UsoView />}

            {aba === 'logs' && (
            <>
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col md:flex-row gap-4 items-center transition-colors">
                <div className="flex-1 w-full relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por ação ou nome da negociação..."
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg outline-none focus:border-primary transition-colors text-sm"
                    />
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    {/* User Filter */}
                    <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900">
                        <User size={16} className="text-gray-400" />
                        <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="bg-transparent border-none text-sm text-gray-600 dark:text-gray-300 outline-none w-40 cursor-pointer"
                        >
                            <option value="all" className="dark:bg-gray-800">Todos os usuários</option>
                            {profiles.map(p => (
                                <option key={p.id} value={p.id} className="dark:bg-gray-800">
                                    {p.full_name || p.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Período:</span>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary"
                            />
                        </div>
                        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>
                        <button
                            onClick={() => {
                                const now = new Date();
                                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                setStartDate(firstDay.toISOString().split('T')[0]);
                                setEndDate(lastDay.toISOString().split('T')[0]);
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                        >
                            <Calendar size={14} />
                            Mês Atual
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider">
                                <th className="px-6 py-4">Data/Hora</th>
                                <th className="px-6 py-4">Usuário</th>
                                <th className="px-6 py-4">Ação</th>
                                <th className="px-6 py-4">Detalhes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center">
                                        <LoadingSpinner label="Carregando registros..." size="sm" />
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhum registro encontrado para os filtros selecionados.
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {new Date(log.created_at).toLocaleString('pt-BR')}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
                                                        <User size={12} />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {(log as any).profiles?.full_name || 'Usuário Desconhecido'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-xs font-mono text-gray-700 dark:text-gray-300 font-bold">
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 font-mono w-1/3">
                                                {log.details?.negociacao_id && negotiationNames[String(log.details.negociacao_id)] && (
                                                    <div className="mb-2 font-sans">
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-bold border border-primary/20">
                                                            Negociação: {negotiationNames[String(log.details.negociacao_id)]} (ID: {log.details.negociacao_id})
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="max-w-xs xl:max-w-md truncate" title={JSON.stringify(log.details, null, 2)}>
                                                    {JSON.stringify(log.details)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {hasMore && (
                                        <tr className="bg-gray-50/50">
                                            <td colSpan={4} className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => fetchLogs(false)}
                                                    disabled={loadingMore}
                                                    className="text-primary text-sm font-semibold hover:underline disabled:opacity-50 flex items-center justify-center gap-2 w-full"
                                                >
                                                    {loadingMore ? (
                                                        <>
                                                            <LoadingSpinner label="Carregando..." size="sm" />
                                                        </>
                                                    ) : (
                                                        'Carregar mais 100 registros'
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default LogsView;
